<script setup lang="ts">
import { useButtons } from '~/components/BaseForm/compose/use-buttons'
import groupFields from '~/components/Contact/form/groupField'
import { useURL } from '~/composables/useURL'

import type { VueMessageType } from '#i18n'
import type {
  ContactRequest,
  KindRequest,
  ContactRequestPayload,
} from 'lc-repository/entities'
import type { BaseFormConfigForm, MutationType } from '../BaseForm/types'
import type { SendMutationType } from '~/helpers/BaseForm/sendMutation'

const emits = defineEmits<{ 'on-success': [boolean] }>()
defineSlots<{ title(): any }>()

const { $httpCookies, $lcRepositories } = useNuxtApp()
const { getBaseURL } = useURL()
const { t, rt, tm } = useI18n({ useScope: 'local' })
const localePath = useLocalePath()
const { trackEvent } = useTracking()
const { user, formattedPhone } = useUser()
const { headerNavigation } = useHeaderNavigation()
const { destinationOptions: mutationOptions } = useSearchDestination()
const { btnPrimary } = useButtons()
btnPrimary.text = computed(() => t('form.cta'))
if (btnPrimary.attrs) {
  btnPrimary.attrs.class = 'w-fit'
}

const previousUrl = computed(
  () => `${getBaseURL()}${headerNavigation.value.urlFrom}`,
)

const showValidation = ref(false)
const initialData = reactive({
  budget: '',
  dates: { startDate: '', endDate: '' },
  destination: '',
  email: user.value.email || '',
  guests: 0,
  firstname: user.value.firstName || '',
  kind: '' as KindRequest,
  knowWhereToLeave: '',
  lastname: user.value.lastName || '',
  message: '',
  newsletter: false,
  phone: formattedPhone.value,
})

const isQualifiedInquiry = computed(
  () =>
    initialData.kind === 'inquiry' && initialData.knowWhereToLeave === 'yes',
)
const validator = (keys: Array<keyof typeof initialData>) =>
  keys.every((key) => Boolean(initialData[key]))
const validCommonFields = computed(() =>
  validator(['email', 'firstname', 'lastname', 'message', 'phone']),
)
const validInquiry = computed(() =>
  isQualifiedInquiry.value ? validator(['destination']) : true,
)
const disabledPrimaryButton = computed(
  () => !validCommonFields.value || !validInquiry.value,
)
const confirmationLines = computed<VueMessageType[]>(() =>
  tm(`validation.${initialData.kind}.message`),
)

const groupFieldsForm = reactive(groupFields())
const configForm = reactive<BaseFormConfigForm>({
  groupFields: {
    contact: {
      hidden: computed(() => !initialData.kind),
    },
    inquiry: {
      hidden: computed(() => initialData.kind !== 'inquiry'),
    },
    feedback: {
      hidden: computed(() => initialData.kind !== 'feedback'),
    },
    press: {
      hidden: computed(() => initialData.kind !== 'press'),
    },
    support: {
      hidden: computed(() => initialData.kind !== 'support'),
    },
  },
  fields: {
    destination: {
      hidden: computed(() => !isQualifiedInquiry.value),
      mutationOptions,
    },
    dates: {
      hidden: computed(() => !isQualifiedInquiry.value),
    },
    budget: {
      hidden: computed(() => !isQualifiedInquiry.value),
    },
    guests: {
      hidden: computed(() => !isQualifiedInquiry.value),
    },
    newsletter: {
      hidden: computed(() => !initialData.kind),
      attr: {
        label: '',
        labelHtml: computed(
          () =>
            `text:#{global.subscribe_newsletter},link:#{global.privacyPolicyWithPoint}[href_blank:${localePath(
              'legal-privacyPolicy',
            )}][class:text-md underline text-gray-700]`,
        ),
      },
    },
  },
})

const createContactRequest = async (payload: ContactRequestPayload) => {
  if (initialData.newsletter) {
    await $lcRepositories.newsletterEmails
      .subscribeNewsletter({
        email: payload.contact.email,
      })
      .catch(() => undefined)
  }
  return $lcRepositories.contacts.createContactRequest({ payload })
}

const mutation: MutationType<typeof initialData, ContactRequestPayload> = (
  data,
) => ({
  request: createContactRequest,
  input: {
    contact: {
      email: data.email,
      first_name: data.firstname,
      kind: data.kind,
      last_name: data.lastname,
      last_url_visited: previousUrl.value,
      message: data.message,
      phone: data.phone,
      ...(isQualifiedInquiry.value
        ? {
            budget: data.budget,
            check_in_date: data.dates.startDate,
            check_out_date: data.dates.endDate,
            destination_id: data.destination,
            number_of_guests: data.guests,
          }
        : {}),
    },
    hubspotutk: $httpCookies?.get('hubspotutk'),
  },
})
const onSubmit: () => SendMutationType<ContactRequest>['onSubmit'] = () => ({
  success: () => {
    showValidation.value = ['feedback', 'inquiry', 'press', 'support'].includes(
      initialData.kind,
    )
    const contactType: Record<KindRequest, string> = {
      feedback: 'share feedback',
      inquiry: 'plan a stay',
      press: 'pr',
      support: 'other',
    }
    trackEvent({
      event: 'form_sent_contact',
      contact_type: contactType[initialData.kind] ?? 'other',
      hash: {
        email: initialData.email,
        phone: initialData.phone,
      },
    })
    emits('on-success', showValidation.value)
  },
  error: (err) => {
    console.warn(err)
  },
})
</script>

<template>
  <div v-if="showValidation" class="flex flex-col text-md">
    <p class="!mb-1 font-bold">
      {{ t(`validation.${initialData.kind}.name`) }}
    </p>
    <p v-for="(message, i) in confirmationLines" :key="`${i}`">
      {{ rt(message) }}
      <br />
    </p>
    <p
      class="mt-6 flex w-fit items-center justify-center gap-2 rounded-sm bg-secondary-100 px-3 py-2 text-secondary-500"
    >
      <span class="font-medium">
        {{ t(`validation.${initialData.kind}.confirmation`) }}
      </span>
      <BaseIcon name="checkCircle" :size="1.25" />
    </p>
  </div>

  <BaseForm
    v-else
    container-button-class="flex-wrap mt-2 sm:mt-10"
    id-form="contact-form"
    :button-primary="initialData.kind ? btnPrimary : undefined"
    :config-form="configForm"
    :disabled-primary-button="disabledPrimaryButton"
    :group-fields="groupFieldsForm"
    :initial-data="initialData"
    :mutation="mutation"
    :on-submit="onSubmit"
  />
</template>

<style scoped>
:deep(.base-radio) {
  @apply checked:bg-white checked:border-secondary-400 checked:border-[6px];
}
:deep(.base-form--title) {
  @apply mb-4 font-sansSerif text-gray-700 text-base font-normal;
}
:deep(.base-checkbox-label) {
  @apply items-start font-normal leading-normal;
}
:deep(.base-checkbox-label > input) {
  @apply mt-0.5;
}
p {
  @apply mb-0;
}
</style>

<i18n lang="yaml">
fr:
  form:
    cta: Envoyer ma demande
  validation:
    feedback:
      name: Partager un retour d'expérience
      message:
        - Votre message a bien été pris en compte.
        - Merci d'avoir pris le temps de partager votre expérience.
        - Notre équipe dédiée vous contactera dans les meilleurs délais.
      confirmation: Message envoyé
    inquiry:
      name: Prévoir un séjour
      message:
        - Votre demande a bien été prise en compte.
        - Un de nos conseillers vous contactera d'ici un jour ouvré.
      confirmation: Demande envoyée
    support:
      name: Autre demande
      message:
        - Votre message a bien été pris en compte.
        - Un de nos conseillers vous contactera d'ici un jour ouvré.
      confirmation: Message envoyé
    press:
      name: Contacter le service de presse
      message:
        - Votre demande a bien été prise en compte.
        - Nous reviendrons vers vous prochainement.
      confirmation: Demande envoyée
en:
  form:
    cta: Send a request
  validation:
    feedback:
      name: Share feedback
      message:
        - Your message has been received successfully.
        - Thank you for taking the time to share your experience.
        - Our dedicated team will contact you as soon as possible.
      confirmation: Message sent
    inquiry:
      name: Plan a stay
      message:
        - Your request has been successfully received.
        - One of our advisors will contact you within one working day.
      confirmation: Request sent
    support:
      name: Other request
      message:
        - Your message has been received.
        - One of our advisors will contact you within one working day.
      confirmation: Message sent
    press:
      name: Contact the press office
      message:
        - Your request has been successfully received.
        - We will get back to you shortly.
      confirmation: Request sent
</i18n>
